'use client'

import { CartProvider, ShopifyProvider } from '@shopify/hydrogen-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState } from 'react'

import { OnboardingProvider } from '@/components/common/Onboarding/Onboarding.Provider'
import { useAppContext } from '@/context/AppContext'
import { CustomerProvider } from '@/context/customer/CustomerContext'
import { HeaderProvider } from '@/context/HeaderContext'
import { RouteHistoryProvider } from '@/context/RouteHistoryContext'
import { env } from '@/env'
import { CART_FRAGMENT } from '@/graphql/storefront/Cart'
import { Analytics } from '@/lib/analytics'
import { CartActions } from '@/lib/analytics/Analytics.constants'

import type { CartActionsType } from '@/lib/analytics/Analytics.constants'
import type { OnboardingCookie } from '@/types/cookies'
import type { Customer } from '@/types/shopify/customer-account'

type InitialData = {
  customer: {
    customer: Customer | null
    isLoggedIn: boolean
    customerAccessToken:
      | {
          accessToken: string
          refreshToken: string
          token_type: string
          expiresIn: number
          idToken: string
        }
      | undefined
  }
  onboardingCookie?: OnboardingCookie
}

const SHOPIFY_PROVIDER_CONFIG = {
  storeDomain: env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN,
  storefrontToken: env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_TOKEN,
  storefrontApiVersion: env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_API_VERSION,
  countryIsoCode: 'AU',
  languageIsoCode: 'EN',
} as const

export const Providers = ({ children, initialData }: { children: React.ReactNode; initialData: InitialData }) => {
  // Cookie must be cleared via a client-initiated server action; see ProductProvider

  // The customer and the initial onboarding must be fetched server-side before the ProductProvider is rendered to ensure the DOM is rendering correctly due to logic requirements
  // If the onboarding cookie is fetched but customer is not fetched yet, it will trigger the onboarding slideout incorrectly.
  const { setCartIsOpen, automaticCartOpen, setAutomaticCartOpen, session } = useAppContext()

  const [cartAction, setCartAction] = useState<null | CartActionsType>(null) // This is used to track the cart action and send the event to Google Analytics in AnalyticsCart.tsx

  // Create a new QueryClient instance for each session
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60 * 5, // 5 minutes
            gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
            retry: 1,
            refetchOnWindowFocus: false,
          },
          mutations: {
            retry: 1,
          },
        },
      }),
  )

  return (
    <QueryClientProvider client={queryClient}>
      <ShopifyProvider {...SHOPIFY_PROVIDER_CONFIG}>
        <CartProvider
          cartFragment={CART_FRAGMENT}
          onLineAddComplete={() => {
            setCartIsOpen(true)
            setCartAction(CartActions.ADD)
          }}
          onCreateComplete={() => {
            if (automaticCartOpen) {
              setCartIsOpen(true)
              setAutomaticCartOpen(false)
              setCartAction(CartActions.ADD)
            }
          }}
          onLineRemoveComplete={() => {
            setCartAction(CartActions.REMOVE)
          }}
          onLineUpdateComplete={() => {
            setCartAction(CartActions.UPDATE)
          }}
          customerAccessToken={session.customerAccessToken?.accessToken || ''}
        >
          <CustomerProvider customerAccessToken={initialData.customer.customerAccessToken?.accessToken || ''} initialData={initialData.customer.customer}>
            <OnboardingProvider onboardingCookie={initialData.onboardingCookie}>
              <Analytics.Cart action={cartAction} clear={() => setCartAction(null)} />
              <HeaderProvider>
                <RouteHistoryProvider>{children}</RouteHistoryProvider>
              </HeaderProvider>
            </OnboardingProvider>
          </CustomerProvider>
        </CartProvider>
      </ShopifyProvider>
    </QueryClientProvider>
  )
}
